import 'filepond/dist/filepond.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

import {renderMath} from 'src/entries/my_katex';
import {ProblemChat} from 'src/entries/exam/_problemChat';
import {EVENT_TYPE, studApi} from 'src/entries/exam/_studApi';

import {create, registerPlugin} from 'filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImageEdit from 'filepond-plugin-image-edit';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';

const submit_button_template = document.getElementById('submit_button_template');
const MAX_WRITTEN_UPLOAD_BYTES = 30 * 1024 * 1024;
const BROWSER_PREVIEWABLE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp']);

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview,
  FilePondPluginImageExifOrientation,
  FilePondPluginImageEdit,
  FilePondPluginImageResize,
  FilePondPluginImageTransform,
);

async function sha256Hex(blob) {
  const inputBuffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', inputBuffer);
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

async function uploadBlobToPresignedUrl(uploadUrl, blob, contentType) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || 'application/octet-stream',
    },
    body: blob,
  });
  if (!response.ok) {
    throw new Error(`upload_failed_${response.status}`);
  }
}

function getFileSizeMessage() {
  return `File is too large. Limit: ${MAX_WRITTEN_UPLOAD_BYTES / (1024 * 1024)} MB.`;
}

function getFilenameExtension(value) {
  const raw = String(value || '').trim().toLowerCase();
  const match = raw.match(/\.([a-z0-9]+)(?:[?#].*)?$/i);
  return match ? match[1] : '';
}

function isBrowserPreviewableImage(fileOrName) {
  if (!fileOrName) {
    return false;
  }
  const fileType = String(fileOrName.type || '').trim().toLowerCase();
  if (fileType.startsWith('image/') && !['image/heic', 'image/heif'].includes(fileType)) {
    return true;
  }
  return BROWSER_PREVIEWABLE_EXTENSIONS.has(getFilenameExtension(fileOrName.name || fileOrName.filename || fileOrName));
}

export class PhotoUploader {
  constructor(container, config, token, options = {}) {
    if (!container) {
      throw new Error('Container is required.');
    }
    this.config = config;
    this.token = token;
    this.problemId = config.id;
    this.problemNum = config.order_index;
    this.container = container;
    this.maxLongSide = Number(options.maxLongSide || 1920);
    this.onUploadSuccess = typeof options.onUploadSuccess === 'function' ? options.onUploadSuccess : null;
    this.onDeleteSuccess = typeof options.onDeleteSuccess === 'function' ? options.onDeleteSuccess : this.onUploadSuccess;
    this.existingUploads = Array.isArray(options.existingUploads) ? options.existingUploads : [];
    this.enableSupportChat = options.enableSupportChat !== false;
    this.transformUploads = options.transformUploads !== false;
    this.fallbackUploadHandler = typeof options.uploadHandler === 'function'
      ? options.uploadHandler
      : async (formData, problemId) => studApi.save_photo_answer(formData, problemId);
    this.initUploadHandler = typeof options.initUploadHandler === 'function'
      ? options.initUploadHandler
      : async (payload) => studApi.init_written_upload(payload);
    this.finalizeUploadHandler = typeof options.finalizeUploadHandler === 'function'
      ? options.finalizeUploadHandler
      : async (payload) => studApi.finalize_written_upload(payload);
    this.verifyUploadHandler = typeof options.verifyUploadHandler === 'function'
      ? options.verifyUploadHandler
      : null;
    this.deleteUploadHandler = typeof options.deleteUploadHandler === 'function'
      ? options.deleteUploadHandler
      : async (writtenSolutionId) => studApi.delete_written_upload(writtenSolutionId);
    this.directUploadMaxAttempts = Math.max(1, Number(options.directUploadMaxAttempts || 3));
    this.onEvent = typeof options.onEvent === 'function'
      ? options.onEvent
      : (eventType, problemId, payload) => studApi.test_event(eventType, problemId, payload);
    this.existingUploadPreviewResolver = typeof options.existingUploadPreviewResolver === 'function'
      ? options.existingUploadPreviewResolver
      : null;
    this.canDeleteExistingUploads = options.canDeleteExistingUploads !== false && typeof this.deleteUploadHandler === 'function';
    this.canDeleteUploadItem = typeof options.canDeleteUploadItem === 'function'
      ? options.canDeleteUploadItem
      : () => this.canDeleteExistingUploads;

    const uploaderElement = document.createElement('section');
    uploaderElement.id = `problem_${this.problemNum}`;
    uploaderElement.className = 'problem-container';
    uploaderElement.innerHTML = `
      <div class="problem-text">
        <div class="clear-both"></div>
        <div class="prob-header-row">
          <span class="problem-title">Question ${this.problemNum}.</span> <span class="problem-name">${this.config.title_snapshot || ''}</span>
        </div>
        <p>${this.config.html_text_snapshot}...</p>
        <div class="clear-both"></div>
        <div class="prob-header-row"></div>
        <input class="input_elem" type="file" multiple="true" accept="image/*"/>
        <div class="place_for_upload"></div>
        <div class="uploaded_saved_info text-sm text-gray-600 mt-2"></div>
        <div class="uploaded_saved_gallery hidden mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"></div>
      </div>
      <div class="problem-chat-container"></div>
    `;

    this.input = uploaderElement.querySelector('.input_elem');
    this.problem_chat_container = uploaderElement.querySelector('.problem-chat-container');
    if (this.enableSupportChat) {
      this.problem_chat_component = new ProblemChat(this.problem_chat_container, this.token, this.problemId);
    } else if (this.problem_chat_container) {
      this.problem_chat_container.remove();
    }
    this.place_for_upload = uploaderElement.querySelector('.place_for_upload');
    this.uploaded_saved_info = uploaderElement.querySelector('.uploaded_saved_info');
    this.uploaded_saved_gallery = uploaderElement.querySelector('.uploaded_saved_gallery');
    const buttonClone = submit_button_template.cloneNode(true);
    buttonClone.removeAttribute('id');
    buttonClone.classList.remove('hidden');
    this.place_for_upload.appendChild(buttonClone);
    this.upload_button = buttonClone.querySelector('.upload_button');
    this.ready_button_text = buttonClone.querySelector('.ready_button_text');
    this.ready_button_loading = buttonClone.querySelector('.ready_button_loading');
    this.upload_button.style.display = 'none';
    this.ready_button_text.textContent = 'Uploads start automatically';

    this.container.appendChild(uploaderElement);
    renderMath(this.container);

    this.pond = create(this.input, {
      allowMultiple: true,
      instantUpload: true,
      allowProcess: true,
      allowRevert: true,
      allowImagePreview: true,
      imagePreviewFilterItem: (item) => isBrowserPreviewableImage(item?.file || item),
      allowImageCrop: this.transformUploads,
      allowImageEdit: this.transformUploads,
      imagePreviewHeight: 300,
      imageResizeTargetWidth: this.transformUploads ? this.maxLongSide : null,
      imageResizeTargetHeight: this.transformUploads ? this.maxLongSide : null,
      imageResizeUpscale: this.transformUploads ? false : null,
      imageResizeMode: this.transformUploads ? 'contain' : null,
      imageTransformBeforeCreateBlob: this.transformUploads
        ? (canvas) => new Promise((resolve) => {
          const ctx = canvas.getContext('2d');
          ctx.font = '48px serif';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
          const text = `${this.problemNum} (${this.problemId})`;
          const textMetrics = ctx.measureText(text);
          ctx.fillRect(10, canvas.height - 68, textMetrics.width + 20, 60);
          ctx.fillStyle = 'black';
          ctx.fillText(text, 20, canvas.height - 20);
          resolve(canvas);
        })
        : null,
      imageTransformOutputMimeType: this.transformUploads ? 'image/webp' : null,
      imageTransformOutputQuality: this.transformUploads ? 0.92 : null,
      acceptedFileTypes: ['image/*'],
      beforeAddFile: (item) => {
        const fileSize = Number(item?.fileSize || item?.file?.size || 0);
        if (fileSize > MAX_WRITTEN_UPLOAD_BYTES) {
          alert(getFileSizeMessage());
          return false;
        }
        return true;
      },
      labelIdle: '<span class="filepond--label-action">Browse or make photos</span> of your solutions (or drag & drop them)',
      server: {
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          let aborted = false;
          const controller = new AbortController();
          const run = async () => {
            try {
              const savedFile = await this.processSingleFile(file, progress, controller.signal);
              if (aborted) {
                return;
              }
              load(String(savedFile?.id || savedFile?.written_solution_id || ''));
            } catch (err) {
              if (aborted) {
                return;
              }
              error(err?.message || 'Upload failed');
            }
          };
          void run();
          return {
            abort: () => {
              aborted = true;
              controller.abort();
              abort();
            },
          };
        },
        revert: (uniqueFileId, load, error) => {
          const writtenSolutionId = Number(uniqueFileId || 0);
          if (!writtenSolutionId) {
            load();
            return;
          }
          Promise.resolve(this.deleteSavedUpload(writtenSolutionId))
            .then(() => load())
            .catch((err) => error(err?.message || 'Delete failed'));
        },
      },
    });

    this.pond.on('addfile', (pondError, fileItem) => {
      if (!pondError && fileItem) {
        const eventPayload = `${fileItem.fileSize}_${fileItem.filename}`;
        this.emitEvent(EVENT_TYPE.IMAGE_SELECTED, eventPayload);
      }
    });
    this.pond.on('processfile', (pondError, fileItem) => {
      if (!pondError && fileItem) {
        this.emitEvent(EVENT_TYPE.IMAGE_SENT, fileItem.filename || '');
        this.setExistingUploads(this.existingUploads);
      }
    });
    this.pond.on('processfileabort', (fileItem) => {
      this.emitEvent(EVENT_TYPE.IMAGE_SEND_ERROR, fileItem?.filename || 'aborted');
      this.setExistingUploads(this.existingUploads);
    });
    this.pond.on('processfileerror', (fileItem) => {
      this.emitEvent(EVENT_TYPE.IMAGE_SEND_ERROR, fileItem?.filename || 'failed');
      this.setExistingUploads(this.existingUploads);
    });
    this.pond.on('removefile', (pondError, fileItem) => {
      if (pondError) {
        return;
      }
      const serverId = Number(fileItem?.serverId || 0);
      if (serverId) {
        this.emitEvent(EVENT_TYPE.IMAGE_SEND_ERROR, `deleted:${serverId}`);
      }
      this.setExistingUploads(this.existingUploads);
    });

    this.setExistingUploads(this.existingUploads);
  }

  async processSingleFile(file, progress, signal) {
    const blob = file instanceof Blob ? file : file?.file;
    const filename = String(file?.name || file?.filename || `upload_${Date.now()}`);
    const contentType = String(blob?.type || file?.type || 'application/octet-stream');
    const size = Number(blob?.size || file?.size || 0);
    if (size > MAX_WRITTEN_UPLOAD_BYTES) {
      throw new Error(getFileSizeMessage());
    }
    if (signal?.aborted) {
      throw new Error('Upload aborted');
    }
    progress(true, 0, size);
    const fileHash = await sha256Hex(blob);
    progress(true, Math.min(size / 3, size), size);

    let directError = null;
    for (let attempt = 1; attempt <= this.directUploadMaxAttempts; attempt++) {
      if (signal?.aborted) {
        throw new Error('Upload aborted');
      }
      try {
        const savedFile = await this.processSingleFileDirect({
          blob,
          filename,
          contentType,
          size,
          fileHash,
          progress,
        });
        progress(true, size, size);
        if (this.onUploadSuccess) {
          await this.onUploadSuccess(this.problemId, {status: 'ok', saved_file: savedFile});
        }
        return savedFile;
      } catch (error) {
        directError = error;
        if (attempt >= this.directUploadMaxAttempts) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      }
    }

    try {
      const formData = new FormData();
      formData.append('file_0', blob, filename);
      const fallbackPayload = await this.fallbackUploadHandler(formData, this.problemId);
      if (fallbackPayload?.status !== 'ok' || !Array.isArray(fallbackPayload?.saved_files) || !fallbackPayload.saved_files.length) {
        throw new Error(fallbackPayload?.msg || directError?.message || 'Upload failed');
      }
      progress(true, size, size);
      if (this.onUploadSuccess) {
        await this.onUploadSuccess(this.problemId, fallbackPayload);
      }
      return {
        id: fallbackPayload.saved_files[0].written_solution_id,
        written_solution_id: fallbackPayload.saved_files[0].written_solution_id,
      };
    } catch (fallbackError) {
      throw new Error(fallbackError?.message || directError?.message || 'Upload failed');
    }
  }

  async processSingleFileDirect({blob, filename, contentType, size, fileHash, progress}) {
    const initPayload = await this.initUploadHandler({
      test_instance_task_id: this.problemId,
      filename,
      content_type: contentType,
      file_size: size,
      file_hash: fileHash,
    });
    if (initPayload?.status !== 'ok' || !initPayload?.upload_url || !initPayload?.key) {
      throw new Error(initPayload?.msg || 'Direct upload init failed');
    }
    await uploadBlobToPresignedUrl(initPayload.upload_url, blob, initPayload.content_type || contentType);
    progress(true, Math.min((size * 2) / 3, size), size);
    const finalizePayload = await this.finalizeUploadHandler({
      test_instance_task_id: this.problemId,
      filename,
      content_type: contentType,
      file_size: size,
      file_hash: fileHash,
      s3_key: initPayload.key,
    });
    if (finalizePayload?.status !== 'ok' || !finalizePayload?.saved_file) {
      throw new Error(finalizePayload?.msg || 'Direct upload finalize failed');
    }
    await this.verifySavedUpload(finalizePayload.saved_file);
    return finalizePayload.saved_file;
  }

  async verifySavedUpload(savedFile) {
    if (!this.verifyUploadHandler) {
      return;
    }
    const writtenSolutionId = Number(savedFile?.id || savedFile?.written_solution_id || 0);
    if (!writtenSolutionId) {
      throw new Error('Direct upload verification failed: missing saved file id');
    }
    const verification = await this.verifyUploadHandler(writtenSolutionId, savedFile);
    if (verification?.status === 'ok') {
      return;
    }
    try {
      await this.deleteUploadHandler(writtenSolutionId);
    } catch (deleteError) {
      console.error(deleteError);
    }
    throw new Error(verification?.msg || 'Direct upload verification failed');
  }

  async deleteSavedUpload(writtenSolutionId) {
    const response = await this.deleteUploadHandler(writtenSolutionId);
    if (response?.status !== 'ok') {
      throw new Error(response?.msg || 'Delete failed');
    }
    if (this.onDeleteSuccess) {
      await this.onDeleteSuccess(this.problemId, response);
    }
    return response;
  }

  emitEvent(eventType, payload = null) {
    try {
      this.onEvent(eventType, this.problemId, payload);
    } catch (error) {
      console.error(error);
    }
  }

  setExistingUploads(uploads) {
    this.existingUploads = Array.isArray(uploads) ? uploads : [];
    const pondServerIds = new Set(
      (this.pond?.getFiles?.() || [])
        .map((fileItem) => Number(fileItem?.serverId || 0))
        .filter((id) => id > 0)
    );
    const visibleUploads = this.existingUploads.filter((item) => !pondServerIds.has(Number(item?.id || 0)));
    const count = visibleUploads.length;
    if (!this.uploaded_saved_info) {
      return;
    }
    if (count === 0) {
        this.uploaded_saved_info.innerHTML = '';
      if (this.uploaded_saved_gallery) {
        this.uploaded_saved_gallery.innerHTML = '';
        this.uploaded_saved_gallery.classList.add('hidden');
      }
      return;
    }
    this.uploaded_saved_info.innerHTML = `<div><b>Already saved for this task:</b> ${count}</div>`;
    if (!this.existingUploadPreviewResolver || !this.uploaded_saved_gallery) {
      return;
    }

    this.uploaded_saved_gallery.innerHTML = '';
    visibleUploads.forEach((item, index) => {
      const preview = this.existingUploadPreviewResolver(item);
      const card = document.createElement('figure');
      card.className = 'bg-gray-50 border border-gray-200 rounded-md p-2';

      const previewUrl = preview?.webpUrl || (isBrowserPreviewableImage(item?.orig_filename || preview?.originalUrl) ? preview?.originalUrl : '');
      if (previewUrl) {
        const img = document.createElement('img');
        img.className = 'w-full h-48 object-contain bg-white rounded';
        img.alt = item?.orig_filename || `saved upload ${index + 1}`;
        img.loading = 'lazy';
        img.src = previewUrl;
        img.onerror = () => {
          if (preview?.originalUrl && preview.originalUrl !== previewUrl && isBrowserPreviewableImage(item?.orig_filename || preview.originalUrl)) {
            img.onerror = null;
            img.src = preview.originalUrl;
            return;
          }
          img.remove();
        };
        card.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'w-full h-48 flex items-center justify-center bg-white rounded text-xs text-gray-500 text-center px-3';
        placeholder.textContent = 'Preview unavailable until conversion finishes';
        card.appendChild(placeholder);
      }

      const caption = document.createElement('figcaption');
      caption.className = 'mt-2 text-xs text-gray-600 break-all';
      caption.textContent = item?.orig_filename || item?.img_hash || `saved upload ${index + 1}`;
      card.appendChild(caption);

      if (this.canDeleteUploadItem(item) && item?.id) {
        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'mt-2 text-xs text-red-600 hover:text-red-800';
        deleteBtn.textContent = 'Delete';
        deleteBtn.addEventListener('click', async () => {
          deleteBtn.disabled = true;
          try {
            await this.deleteSavedUpload(Number(item.id));
          } catch (error) {
            alert(error?.message || 'Delete failed');
            deleteBtn.disabled = false;
          }
        });
        card.appendChild(deleteBtn);
      }

      this.uploaded_saved_gallery.appendChild(card);
    });
    if (!visibleUploads.length) {
      this.uploaded_saved_gallery.classList.add('hidden');
      return;
    }
    this.uploaded_saved_gallery.classList.remove('hidden');
  }

  destroy() {
    if (this.pond && typeof this.pond.destroy === 'function') {
      this.pond.destroy();
    }
  }
}
